import {useMutation, useQuery, useSuspenseQueries} from '@tanstack/react-query';

import {isIOS} from '@zzng/common/utils/agent';

import {deleteCard, getHome, getHomeCards, getHomeRecommendCards} from '@/root/apis/home';
import {HomeCard, HomeCardWithTitle} from '@/root/models/home';

import {CardType} from '@/shared/constants/cardTypes';

export const homeKeys = {
  all: ['home'] as const,
  cards: () => [...homeKeys.all, 'cards'],
  recommendCards: () => [...homeKeys.all, 'recommendCards'],
  cardIntro: (cardType: CardType) => [...homeKeys.all, cardType]
};

const homeQueryOptions = {
  queryKey: homeKeys.all,
  queryFn: getHome,
  staleTime: 2000,
  refetchOnWindowFocus: true
};

const homeCardsQueryOptions = {
  queryKey: homeKeys.cards(),
  queryFn: () => {
    if (isIOS) {
      window.digitalCard?.updateWidgetData?.();
    }

    return getHomeCards();
  },
  select: (cards: HomeCard[]): HomeCardWithTitle[] => {
    return cards.map((card) => ({
      ...card,
      title: card.groupedCards[0].title,
      subTitle: card.groupedCards[0].subTitle,
      // 그룹 카드는 만료 표시하지 않음
      expired: card.groupedCards.length === 1 && card.groupedCards[0].expired
    }));
  },
  staleTime: 2000,
  refetchOnWindowFocus: true
};

const recommendCardsQueryOptions = {
  queryKey: homeKeys.recommendCards(),
  queryFn: () => getHomeRecommendCards(),
  staleTime: 2000,
  refetchOnWindowFocus: true
};

export const useHome = () => {
  const [home, cards, recommendCards] = useSuspenseQueries({
    queries: [homeQueryOptions, homeCardsQueryOptions, recommendCardsQueryOptions]
  });

  return {
    ...home.data,
    cards: cards.data,
    recommendCards: recommendCards.data
  };
};

export const useHomeCards = () => {
  const {data: cards} = useQuery(homeCardsQueryOptions);

  const deleteCardMuation = useMutation({
    mutationFn: (cardIds: string[]) => deleteCard(cardIds),
    throwOnError: false
  });

  return {
    cards,
    deleteCardMuation
  };
};
